<template>
  <div class="page_title">
    <div class="align py-3 px-4">
      <h1 class="px-1">
        <i class="fa-regular fa-chart-simple icon"></i>
        <span>Relatório de notícias</span>
      </h1>
    </div>
  </div>


  <div v-if="!loading || articles.data" class="align p-4 space_title">
    <div class="card">
      <div class="p-4" id="table_filter">
        <div class="row">
          <div class="col-12 col-md-6 mb-2 mb-md-0">
            <InputText placeholder="Buscar" v-model="filter.search" /></div>
          <div class="col-12 col-md-3 mb-2 mb-md-0">
            <SelectBox
                id="type"
                placeholder="Publisher"
                :items="types"
                v-model="filter.type"
            />
          </div>
          <div class="col-12 col-md-3">
            <InputDatePicker placeholder="Data" v-model="filter.date" range />
          </div>
        </div>
      </div>
      <div class="table_container">
        <table class="table_responsive">
          <thead>
          <tr>
            <th>Título</th>
            <th>Site</th>
            <th class="text-nowrap">Último compartilhamento</th>
            <th v-if="filter.type == 'redator'" class="text-nowrap">Visitas orgânicas</th>
            <th v-if="filter.type == 'redator'" class="text-nowrap">Visitas redes sociais</th>
            <th v-else>Visitas</th>
            <th v-if="filter.type != 'redator'"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(article, key) in articles.data" :key="key">
            <td>
              {{ article.title }}
            </td>
            <td class="text-nowrap">
              <i class="fa-solid fa-globe icon_label me-1"></i>
              {{ article.site_name }}
            </td>
            <td>
              <i class="fa-solid fa-calendar-days icon_label me-1"></i>
              {{ (article.last_share) ? $dateFormat(article.last_share) : null }}
            </td>
            <td v-if="filter.type == 'redator'">
              <i class="fa-solid fa-arrow-pointer icon_label me-1"></i>
              {{ $numberFormat(article.organic_views) }}
            </td>
            <td>
              <i class="fa-solid fa-arrow-pointer icon_label me-1"></i>
              {{ $numberFormat(article.social_views) }}
            </td>
            <td v-if="filter.type != 'redator'" class="actions" width="100">
              <a>
                <button class="btn btn-secondary btn_action_responsive me-2" type="button" @click="openShareModal(article)">
                  <span>Compartilhar</span>
                  <i class="fa-solid fa-share-nodes"></i>
                </button>
                </a>
                <router-link :to="{ name: 'reportArticle', params: { id: article.id_article_wp }, query: { date_start: dateStart, date_end: dateEnd }}">
                  <button class="btn btn-secondary btn_action_responsive" type="button">
                    <span>Detalhes</span>
                    <i class="fa-regular fa-chart-simple"></i>
                  </button>
                </router-link>
            </td>
          </tr>
          <tr class="no_result" v-if="!articles.data || (articles.data && !articles.data.length)">
            <td colspan="10">
              Nenhum resultado encontrado
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>


    <ListPagination class="mt-4" :links="articles.meta.links" />
  </div>

  <ShareModal
    v-model="showShareModal"
    :article="articleToShare"
    @shared="onShared"
    @pageShared="onPageShared"
  />
</template>

<script setup>
import {computed, onBeforeMount, ref, watch} from 'vue';
import {useReportStore} from '@/stores/ReportStore'
import {useArticleStore} from '@/stores/ArticleStore'
import {storeToRefs} from 'pinia';
import ListPagination from '@/components/ListPagination.vue'
import ShareModal from '@/components/ShareModal.vue';
import {inject} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import InputText from '@/components/InputText.vue'
import InputDatePicker from '@/components/InputDatePicker.vue'
import SelectBox from '@/components/SelectBox.vue';
import moment from 'moment';
import axios from 'axios';
import {debounce} from '@/utils/debounce';
import {applyFilters} from "@/utils/applyFilters";

const loader = inject('$loader');
const loading = ref(null);
const firstLoad = ref(true);
const showShareModal = ref(false);
const articleToShare = ref({});
const articleStore = useArticleStore();

const router = useRouter();
const route = useRoute();
const reportStore = useReportStore();
const {articles, filter} = storeToRefs(reportStore);

const types = ref([
  {id: 'publisher', 'name': 'Publisher'},
  {id: 'redator', 'name': 'Redator'},
]);

onBeforeMount(() => {
  filter.value.search = null;

  if (route.query.search) {
    filter.value.search = route.query.search;
  }

  if (route.query.page) {
    filter.value.page = route.query.page;
  }

  if (route.query.type) {
    filter.value.type = route.query.type;
  }

  if (route.query.date_end) {
    filter.value.date = [route.query.date_start, route.query.date_end];
  } else {
    filter.value.date = [moment().utcOffset(-3).format('YYYY-MM-DD'), moment().utcOffset(-3).format('YYYY-MM-DD')];
  }

  loadArticles();
});

const dateStart = computed({
  get: () => filter.value.date[0],
});

const dateEnd = computed({
  get: () => filter.value.date[1],
});

watch(() => route.query.page, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    window.scrollTo(0, 0);
    loadArticles();
  }
});

watch(() => filter.value.search, debounce(() => {
  applyFilters({ search: filter.value.search }, route, router, loadArticles);
}, 500));

watch(() => filter.value.type, debounce(() => {
  applyFilters({type: filter.value.type}, route, router, loadArticles);
}, 500));

watch(() => filter.value.date, debounce(() => {
  applyFilters({date_start: dateStart.value, date_end: dateEnd.value}, route, router, loadArticles);

}, 500));

let cancelTokenSource = null;

async function loadArticles() {
  loader.show();
  loading.value = true;

  if (cancelTokenSource) {
    cancelTokenSource.cancel('Cancelado devido a nova busca');
  }

  cancelTokenSource = axios.CancelToken.source();

  let params = {
    search: filter.value.search,
    page: parseInt(route.query.page),
    limit: 10,
    type: filter.value.type,
    date_start: dateStart.value,
    date_end: dateEnd.value
  }

  await reportStore.getArticles(params, cancelTokenSource);
  loader.hide();
  loading.value = false;
  firstLoad.value = false;
}

async function openShareModal(article) {
  if (!article.id_article) {
    return;
  }

  loader.show();
  const result = await articleStore.getShareArticle(article.id_article);
  loader.hide();

  if (result.success) {
    articleToShare.value = result.data;
    showShareModal.value = true;
  }
}

function onShared() {
  // Atualiza a data de compartilhamento na lista
}

function onPageShared({ pageKey, sharedAt }) {
  // Atualiza a página compartilhada
  if (articleToShare.value.pages) {
    articleToShare.value.pages[pageKey].shared = 1;
    articleToShare.value.pages[pageKey].shared_at = sharedAt;
  }
}
</script>